import type { ActionConfig } from './types';
import { workorderCreateRoute } from './workorderCreateRoute';

export const workorderActions: ActionConfig[] = [
  // =============================================================================
  // EXPORT ACTIONS (Table-level)
  // =============================================================================
  {
    id: 'wo-export-csv',
    name: 'Export to CSV',
    icon: 'download',
    table: 'workorders',
    type: 'EXPORT_VIEW',
    prominence: 'menu',
    order: 1,
    config: {},
  },
  {
    id: 'wo-download-list',
    name: 'Download List',
    icon: 'download',
    table: 'workorders',
    type: 'EXPORT_VIEW',
    prominence: 'menu',
    order: 1,
    config: {},
  },

  // =============================================================================
  // EMAIL ACTIONS
  // =============================================================================
  {
    id: 'wo-send-feedback',
    name: 'Send Feedback',
    icon: 'paper-plane',
    table: 'workorders',
    type: 'EMAIL',
    prominence: 'inline',
    order: 1,
    condition: (row) => Boolean(row.feedback),
    config: {
      subject: (row) => `WO Feedback on ${row.detailedDescription || ''}`,
      body: (row) => String(row.feedback || ''),
    },
  },
  {
    id: 'wo-email-workorder',
    name: 'Email Workorder',
    icon: 'envelope',
    table: 'workorders',
    type: 'EMAIL',
    prominence: 'inline',
    order: 102,
    config: {
      subject: (row) => String(row.detailedDescription || ''),
      body: (row) =>
        `Status: ${row.status}\n WO: ${row.urgent ? 'Urgent ' : ''}${row.detailedDescription || ''}\n - Notes: ${row.notes || ''}\n Location: ${row.location || ''}\n\n Assigned To: ${row.assignedToName || ''}\n Submitted By: ${row.submittedBy || ''}\n Submitted On: ${row.timestamp || ''}${row.billable ? '\n Billable \n' : ''}`,
    },
  },
  {
    id: 'wo-compose-email',
    name: 'Compose Email',
    icon: 'envelope',
    table: 'workorders',
    type: 'EMAIL',
    prominence: 'inline',
    order: 58,
    condition: (row) => Boolean(row.submittedBy) && String(row.submittedBy).includes('@'),
    config: {
      to: (row) => String(row.submittedBy || ''),
    },
  },

  // =============================================================================
  // ADD RECORD ACTIONS
  // =============================================================================
  {
    id: 'wo-auto-log-service',
    name: 'Auto Log Service',
    icon: 'paper-plane',
    table: 'workorders',
    type: 'ADD_RECORD_TO',
    prominence: 'menu',
    order: 1,
    config: {
      targetTable: 'serviceLogs',
      mapping: {
        clientId: 'clientId',
      },
    },
  },
  {
    id: 'wo-create-from-recurrence',
    name: 'Create from Prior Recurrence',
    icon: 'paper-plane',
    table: 'workorders',
    type: 'ADD_RECORD_TO',
    prominence: 'hidden',
    order: 1,
    config: {
      targetTable: 'workorders',
      mapping: {},
    },
  },
  {
    id: 'wo-create-from-recurrence-2',
    name: 'Create from Prior Recurrence 2',
    icon: 'paper-plane',
    table: 'workorders',
    type: 'ADD_RECORD_TO',
    prominence: 'hidden',
    order: 1,
    config: {
      targetTable: 'workorders',
      mapping: {},
    },
  },
  {
    id: 'wo-add',
    name: 'Add',
    icon: 'plus',
    table: 'workorders',
    type: 'ADD_RECORD',
    prominence: 'prominent',
    order: 62,
    excludeContexts: ['detail'],
    config: {},
  },

  // =============================================================================
  // NAVIGATE APP ACTIONS
  // =============================================================================
  {
    id: 'wo-map-my-run',
    name: 'Map Run',
    icon: 'map-marker',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'hidden',
    order: 1,
    condition: (row) => Boolean(row.key), // Simplified: IN([Key],My Workorder Run[Key])
    config: {
      route: '/workorders/my-run/map',
      params: {},
    },
  },
  {
    id: 'wo-view-map-address',
    name: 'View Map',
    icon: 'map-marker',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'hidden',
    order: 40,
    condition: (row) => Boolean(row.mapAddress),
    config: {
      route: '/workorders/:key/map',
      params: { key: 'key' },
    },
  },
  {
    id: 'wo-map-workorder-run',
    name: 'Map Workorder Run',
    icon: 'map-marker',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'hidden',
    order: 42,
    condition: (row) => row.status === '4. Ready for Run',
    config: {
      route: '/workorders/run/:assignedToRun/map',
      params: { assignedToRun: 'assignedToRun' },
    },
  },
  {
    id: 'wo-view-workorder-run',
    name: 'View Workorder Run',
    icon: 'list-ol',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'hidden',
    order: 42,
    condition: (row) => row.status === '4. Ready for Run',
    config: {
      route: '/workorders/run/:assignedToRun',
      params: { assignedToRun: 'assignedToRun' },
    },
  },
  {
    id: 'wo-create-run-map',
    name: 'Create Workorder Run Map',
    icon: 'map-marker',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'hidden',
    order: 43,
    condition: (row) => row.status === '4. Ready for Run',
    config: {
      route: '/workorders/ready-for-run/map',
      params: {},
    },
  },
  {
    id: 'wo-view-client',
    name: 'View Client',
    icon: 'chevron-circle-right',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'inline',
    order: 55,
    condition: (row) => Boolean(row.clientId),
    config: {
      route: '/clients/:clientId',
      params: { clientId: 'clientId' },
    },
  },
  {
    id: 'wo-view-map-address-2',
    name: 'View Map (Address)',
    icon: 'map-marker',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'hidden',
    order: 56,
    condition: (row) => Boolean(row.address),
    config: {
      route: '/workorders/:key/map',
      params: { key: 'key' },
    },
  },
  {
    id: 'wo-re-order',
    name: 'Re-Order',
    icon: 'edit',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'menu',
    order: 80,
    condition: (row) =>
      String(row.status || '').includes('5. Complete') || row.status === '7. Declined',
    config: {
      route: (row: Record<string, unknown>) => workorderCreateRoute(row.clientId),
    },
  },
  {
    id: 'wo-view-floral',
    name: 'View Floral',
    icon: 'paper-plane',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'prominent',
    order: 81,
    condition: (row, user) =>
      row.status === '1. Floral Department' &&
      (user.viewTags.includes('Management Views') ||
        user.viewTags.includes('Sales') ||
        user.email === 'bryan.babcock@thewrightgardner.com'),
    config: {
      route: '/workorders?status=1.+Floral+Department',
      params: {},
    },
  },
  {
    id: 'wo-view-to-order',
    name: 'View To Order',
    icon: 'list-ol',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'prominent',
    order: 82,
    condition: (row) => row.status === '0. New Workorder' || row.status === '1. To Order',
    config: {
      route: '/workorders?status=1.+To+Order',
      params: {},
    },
  },
  {
    id: 'wo-view-received',
    name: 'View Received',
    icon: 'check',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'prominent',
    order: 83,
    condition: (row) => row.status === '2. Ordered' || row.status === '3. Received',
    config: {
      route: '/workorders?status=3.+Received',
      params: {},
    },
  },
  {
    id: 'wo-view-ordered',
    name: 'View Ordered',
    icon: 'tags',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'prominent',
    order: 84,
    condition: (row) => row.status === '2. Ordered' || row.status === '1. To Order',
    config: {
      route: '/workorders?status=2.+Ordered',
      params: {},
    },
  },
  {
    id: 'wo-view-to-process',
    name: 'View To Process',
    icon: 'edit',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'prominent',
    order: 85,
    condition: (row) => row.status === '0. New Workorder' || !row.status,
    config: {
      route: '/workorders?status=0.+New+Workorder',
      params: {},
    },
  },
  {
    id: 'wo-view-ready-for-pickup',
    name: 'View Ready for Pickup',
    icon: 'star',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'inline',
    order: 86,
    condition: (row) => row.status === '0. New Workorder' || row.status === '4. Ready for Pickup',
    config: {
      route: '/workorders?status=4.+Ready+for+Pickup',
      params: {},
    },
  },
  {
    id: 'wo-view-other-at-address',
    name: 'View Other Open WO at Address',
    icon: 'map-marker',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'hidden',
    order: 89,
    condition: (row) => String(row.status || '').includes('4') && Boolean(row.mapAddress),
    config: {
      route: '/workorders?mapAddress=:mapAddress',
      params: { mapAddress: 'mapAddress' },
    },
  },
  {
    id: 'wo-view-in-house',
    name: 'View In House',
    icon: 'home',
    table: 'workorders',
    type: 'NAVIGATE_APP',
    prominence: 'prominent',
    order: 103,
    condition: (row) => row.status === '0. New Workorder' || row.status === '1. To Order',
    config: {
      route: '/workorders?status=1.+In+House',
      params: {},
    },
  },

  // =============================================================================
  // SET COLUMN VALUE ACTIONS - Status Changes
  // =============================================================================
  {
    id: 'wo-mark-incorrect-submission',
    name: 'Mark Incorrect Submission',
    icon: 'times-circle',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'menu',
    order: 1,
    condition: (row) => row.status === '0. New Workorder',
    config: {
      column: 'incorrectSubmission',
      value: true,
    },
  },
  {
    id: 'wo-clear-assigned-to',
    name: 'Clear Assigned To',
    icon: 'times-circle',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'menu',
    order: 5,
    config: {
      column: 'assignedTo',
      value: '',
    },
  },
  {
    id: 'wo-change-status-returned',
    name: 'Change Status to Returned',
    icon: 'chevron-circle-right',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'hidden',
    order: 22,
    config: {
      column: 'status',
      value: '0. Returned',
    },
  },
  {
    id: 'wo-change-status-new',
    name: 'Change Status to New Workorder',
    icon: 'plus',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'hidden',
    order: 23,
    config: {
      column: 'status',
      value: '0. New Workorder',
    },
  },
  {
    id: 'wo-mark-declined',
    name: 'Mark Declined',
    icon: 'thumbs-down',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'menu',
    order: 41,
    condition: (row) => row.status === '6. To Decline',
    config: {
      column: 'status',
      value: '7. Declined',
    },
  },
  {
    id: 'wo-change-status-to-decline',
    name: 'Change Status to To Decline',
    icon: 'paper-plane',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'hidden',
    order: 44,
    config: {
      column: 'status',
      value: '6. To Decline',
    },
  },
  {
    id: 'wo-mark-contact-made',
    name: 'Mark Contact Made',
    icon: 'phone',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'prominent',
    order: 57,
    condition: (row) =>
      row.status === '0. New Workorder' || !row.status || row.status === '1. To Order',
    config: {
      column: 'status',
      value: '1. Contact Made',
    },
  },
  {
    id: 'wo-mark-complete',
    name: 'Mark Complete',
    icon: 'check-square',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'prominent',
    order: 64,
    condition: (row) =>
      ['4. Ready for Pickup', '4. Ready for Run', '4. For South Bay'].includes(
        row.status as string
      ),
    needsConfirmation: true,
    confirmationMessage: 'You are marking this workorder complete. Are you sure?',
    config: {
      column: 'status',
      value: '5. Complete',
    },
  },
  {
    id: 'wo-change-status-floral',
    name: 'Change Status to Floral Department',
    icon: 'paper-plane',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'hidden',
    order: 66,
    config: {
      column: 'status',
      value: '1. Floral Department',
    },
  },
  {
    id: 'wo-change-status-to-order',
    name: 'Mark To Order',
    icon: 'list-ol',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'prominent',
    order: 67,
    condition: (row) => row.status === '0. New Workorder' || !row.status,
    config: {
      column: 'status',
      value: '1. To Order',
    },
  },
  {
    id: 'wo-mark-ready-for-pickup',
    name: 'Mark Ready for Pickup',
    icon: 'star',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'prominent',
    order: 68,
    condition: (row) =>
      ['1. In House', '2. Ordered', '3. Received'].includes(row.status as string) &&
      row.status !== '4. Ready for Pickup',
    needsConfirmation: true,
    confirmationMessage: 'You are marking this workorder Received. Please confirm.',
    config: {
      column: 'status',
      value: '4. Ready for Pickup',
    },
  },
  {
    id: 'wo-mark-for-south-bay',
    name: 'Mark For South Bay',
    icon: 'map-marker',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'prominent',
    order: 70,
    condition: (row) =>
      ['1. In House', '2. Ordered', '3. Received'].includes(row.status as string) &&
      row.status !== '4. Ready for Pickup',
    needsConfirmation: true,
    confirmationMessage: 'You are marking this workorder Received. Please confirm.',
    config: {
      column: 'status',
      value: '4. For South Bay',
    },
  },
  {
    id: 'wo-mark-ready-for-run',
    name: 'Mark Ready for Run',
    icon: 'star',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'hidden',
    order: 71,
    config: {
      column: 'status',
      value: '4. Ready for Run',
    },
  },
  {
    id: 'wo-assign-to-tech',
    name: 'Assign to Tech',
    icon: 'users',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'hidden', // Requires client table join for tech lookup — follow-up
    order: 73,
    condition: (row) =>
      ['4. Ready for Pickup', '3. Received', '4. Sent to STO', '4. Sent to SAC'].includes(
        row.status as string
      ) && row.assignedTo !== row.tech,
    needsConfirmation: true,
    confirmationMessage:
      'You are assigning this workorder to the tech covering the account. Please confirm.',
    config: {
      column: 'assignedTo',
      value: (row: Record<string, unknown>) => row.tech,
    },
  },
  {
    id: 'wo-assign-to-submitter-id',
    name: 'Set Assigned To (Submitter ID)',
    icon: 'users',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'hidden',
    order: 74,
    config: {
      column: 'assignedTo',
      value: (row: Record<string, unknown>) => {
        // submittedBy is a full email — extract ID (email prefix) for assignedTo
        const email = String(row.submittedBy || '');
        return email.includes('@') ? email.split('@')[0] : email;
      },
    },
  },
  {
    id: 'wo-assign-to-submitter-name',
    name: 'Set Assigned To Name (Submitter Name)',
    icon: 'users',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'hidden',
    order: 75,
    config: {
      column: 'assignedToName',
      value: (row: Record<string, unknown>) => row.submittedByName || '',
    },
  },
  {
    id: 'wo-mark-received',
    name: 'Mark Received',
    icon: 'check',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'prominent',
    order: 76,
    condition: (row) =>
      ['2. Ordered', '1. Floral Department', '1. In House'].includes(row.status as string),
    config: {
      column: 'status',
      value: '3. Received',
    },
  },
  {
    id: 'wo-mark-ordered',
    name: 'Mark Ordered',
    icon: 'tags',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'prominent',
    order: 78,
    condition: (row) => String(row.status || '').includes('1. To Order'),
    needsConfirmation: true,
    confirmationMessage:
      'You are about to mark this workorder as Ordered. Please confirm this is what you want to do!',
    config: {
      column: 'status',
      value: '2. Ordered',
    },
  },
  {
    id: 'wo-mark-in-house',
    name: 'Mark In House',
    icon: 'home',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'prominent',
    order: 79,
    condition: (row) => row.status === '0. New Workorder',
    needsConfirmation: true,
    confirmationMessage:
      'You are about to mark this workorder as In House. Please confirm this is what you want to do!',
    config: {
      column: 'status',
      value: '1. In House',
    },
  },
  {
    id: 'wo-flag-as-floral',
    name: 'Flag as Floral',
    icon: 'paper-plane',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'hidden',
    order: 90,
    config: {
      column: 'floral',
      value: true,
    },
  },
  {
    id: 'wo-assign',
    name: 'Assign',
    icon: 'users',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'hidden', // Needs employee picker modal — follow-up
    order: 99,
    condition: (row) =>
      ['4. Ready for Pickup', '3. Received', '4. Ready for Run'].includes(row.status as string),
    config: {
      column: 'assignedTo',
      value: '', // This would be set via input in the actual implementation
    },
  },
  {
    id: 'wo-add-to-run-1-set',
    name: 'Add to WO Run 1',
    icon: 'plus',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'hidden',
    order: 100,
    config: {
      column: 'woRun',
      value: '1',
    },
  },
  {
    id: 'wo-add-to-run-2-set',
    name: 'Add to WO Run 2',
    icon: 'plus',
    table: 'workorders',
    type: 'SET_COLUMN_VALUE',
    prominence: 'hidden',
    order: 101,
    config: {
      column: 'woRun',
      value: '2',
    },
  },

  // =============================================================================
  // COMPOSITE ACTIONS
  // =============================================================================
  {
    id: 'wo-return-wo',
    name: 'Return WO',
    icon: 'times-circle',
    table: 'workorders',
    type: 'COMPOSITE',
    prominence: 'prominent',
    order: 24,
    condition: (row, user) =>
      row.status === '0. New Workorder' &&
      user.email !== row.submittedBy &&
      (user.viewTags.includes('Management Views') ||
        user.viewTags.includes('Workorder Manager Views')),
    config: {
      actions: ['wo-change-status-returned', 'wo-assign-to-submitter-id', 'wo-assign-to-submitter-name'],
    },
  },
  {
    id: 'wo-re-submit',
    name: 'Re-Submit',
    icon: 'paper-plane',
    table: 'workorders',
    type: 'COMPOSITE',
    prominence: 'prominent',
    order: 25,
    condition: (row) => row.status === '0. Returned',
    config: {
      actions: ['wo-clear-assigned-to', 'wo-change-status-new'],
    },
  },
  {
    id: 'wo-mark-to-decline',
    name: 'Mark To Decline',
    icon: 'thumbs-down',
    table: 'workorders',
    type: 'COMPOSITE',
    prominence: 'prominent',
    order: 57,
    condition: (row) =>
      row.status === '0. New Workorder' || !row.status || row.status === '1. To Order',
    config: {
      actions: ['wo-change-status-to-decline'],
    },
  },
  {
    id: 'wo-add-to-run-1',
    name: 'Add to Run 1',
    icon: 'plus',
    table: 'workorders',
    type: 'COMPOSITE',
    prominence: 'prominent',
    order: 59,
    condition: (row) =>
      [
        '1. In House',
        '2. Ordered',
        '3. Received',
        '4. Ready for Pickup',
        '4. Ready for Run',
      ].includes(row.status as string),
    config: {
      actions: ['wo-add-to-run-1-set', 'wo-mark-ready-for-run'],
    },
  },
  {
    id: 'wo-add-to-run-2',
    name: 'Add to Run 2',
    icon: 'plus',
    table: 'workorders',
    type: 'COMPOSITE',
    prominence: 'prominent',
    order: 60,
    condition: (row) =>
      [
        '1. In House',
        '2. Ordered',
        '3. Received',
        '4. Ready for Pickup',
        '4. Ready for Run',
      ].includes(row.status as string),
    config: {
      actions: ['wo-add-to-run-2-set', 'wo-mark-ready-for-run'],
    },
  },
  {
    id: 'wo-send-to-floral',
    name: 'Send to Floral',
    icon: 'paper-plane',
    table: 'workorders',
    type: 'COMPOSITE',
    prominence: 'prominent',
    order: 65,
    condition: (row) => row.status === '0. New Workorder' || !row.status,
    config: {
      actions: ['wo-change-status-floral', 'wo-flag-as-floral'],
    },
  },
  {
    id: 'wo-mark-ready-for-pickup-2',
    name: 'Mark Ready for Pickup',
    icon: 'star',
    table: 'workorders',
    type: 'COMPOSITE',
    prominence: 'prominent',
    order: 69,
    condition: (row) =>
      ['1. In House', '2. Ordered', '3. Received'].includes(row.status as string) &&
      row.status !== '4. Ready for Pickup',
    config: {
      actions: ['wo-mark-ready-for-pickup', 'wo-clear-assigned-to'],
    },
  },
  {
    id: 'wo-mark-ready-for-run-2',
    name: 'Mark Ready for Run',
    icon: 'star',
    table: 'workorders',
    type: 'COMPOSITE',
    prominence: 'prominent',
    order: 72,
    config: {
      actions: ['wo-clear-assigned-to', 'wo-mark-ready-for-run'],
    },
  },
  {
    id: 'wo-mark-received-2',
    name: 'Mark Received',
    icon: 'check',
    table: 'workorders',
    type: 'COMPOSITE',
    prominence: 'prominent',
    order: 77,
    condition: (row) =>
      ['2. Ordered', '1. Floral Department', '1. In House'].includes(row.status as string),
    config: {
      actions: ['wo-mark-received', 'wo-clear-assigned-to'],
    },
  },

  // =============================================================================
  // DELETE / EDIT ACTIONS
  // =============================================================================
  {
    id: 'wo-delete',
    name: 'Delete',
    icon: 'trash',
    table: 'workorders',
    type: 'DELETE_RECORD',
    prominence: 'menu',
    order: 53,
    requiredViews: ['Management Views'],
    needsConfirmation: true,
    confirmationMessage: 'Confirm you want to DELETE this workorder',
    config: {},
  },
  {
    id: 'wo-edit',
    name: 'Edit',
    icon: 'edit',
    table: 'workorders',
    type: 'EDIT_RECORD',
    prominence: 'prominent',
    order: 54,
    config: {},
  },
];
